/* Native Shopify variant resolution for the R02 blue/yellow BootyLift product template. */
(() => {
  class BootyliftProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.stockSelectionKey = null;
        this.cartObserver?.observe(this.querySelector('.product-add'));
        this.galleryObserver?.observe(this.querySelector('.buybox'));
        this.galleryObserver?.observe(this.querySelector('.gallery'));
        this.update(false);
        this.updateGallery();
        return;
      }
      this.initialized = true;
      this.product = JSON.parse(this.querySelector('[data-product]').textContent);
      this.variants = this.product.variants;
      this.lowStock = JSON.parse(this.querySelector('[data-low-stock]')?.textContent || '{}');
      this.mobileGallery = this.querySelector('.mobile-gallery');
      this.optionNames = this.product.options.map(option => typeof option === 'string' ? option : option.name);
      this.sizeIndex = this.optionNames.findIndex(name => /^size$/i.test(name));
      this.lengthIndex = this.optionNames.findIndex(name => /^length$/i.test(name));
      this.colorIndex = this.optionNames.findIndex(name => /^colou?r$/i.test(name));
      const direct = this.variants.find(v => String(v.id) === (this.dataset.selectedVariant || new URL(location.href).searchParams.get('variant')));
      const first = direct || this.variants.find(v => v.available) || this.variants[0];
      this.selected = [...first.options];
      if (!direct) {
        const lengthIndex = this.optionNames.findIndex(name => /^length$/i.test(name));
        const petite = this.variants.find(v => /petite/i.test(v.options[lengthIndex] || ''));
        if (petite) this.selected[lengthIndex] = petite.options[lengthIndex];
        if (this.sizeIndex >= 0) this.selected[this.sizeIndex] = null;
      }
      const requestedColor = new URL(location.href).searchParams.get('color');
      if (!direct && requestedColor && this.variants.some(v => v.options[this.colorIndex] === requestedColor)) this.selected[this.colorIndex] = requestedColor;
      // Bundle offer template: Single / 2-Pack / 3-Pack, every pair in the same size and length, one colour combo per pack.
      this.bundle = this.hasAttribute('data-bundle');
      if (this.bundle) {
        const asked = Number(new URL(location.href).searchParams.get('pack'));
        this.pack = [1, 2, 3].includes(asked) ? asked : Number(this.dataset.defaultPack) || 1;
        this.combos = {};
        [2, 3].forEach(n => {
          const first = this.querySelector(`[data-combo-group="${n}"] [data-combo]`);
          this.combos[n] = first ? first.dataset.combo.split('§') : null;
        });
        if (!this.combos[2] || !this.combos[3]) this.bundle = false;
        else if (this.pack > 1) this.selected[this.colorIndex] = this.combos[this.pack][0];
      }
      const morePayments = this.querySelector('.payment-more');
      if (morePayments) {
        morePayments.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') morePayments.open = true; });
        morePayments.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && !morePayments.contains(document.activeElement)) morePayments.open = false; });
        morePayments.addEventListener('focusout', event => { if (!morePayments.contains(event.relatedTarget)) morePayments.open = false; });
        morePayments.addEventListener('keydown', event => { if (event.key === 'Escape') { morePayments.open = false; morePayments.querySelector('summary').focus(); } });
      }
      this.addEventListener('click', event => this.handleClick(event));
      this.hintSizes();
      this.querySelector('.form-shell').addEventListener('submit', event => { event.preventDefault(); this.purchase(); });
      this.querySelectorAll('dialog:not(.tc-cart-dialog)').forEach(dialog => dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }));
      this.colorGallery = Boolean(this.mobileGallery?.hasAttribute('data-color-gallery'));
      this.update(false);
      if (this.colorGallery) this.showColor(this.selected[this.colorIndex], true);
      else if (direct) this.updateImage(direct);
      else if (requestedColor) this.updateImage(this.matching(this.selected)[0]);
      this.cartObserver = new IntersectionObserver(entries => {
        this.querySelector('.bottom-bar').hidden = entries[0].isIntersecting;
      }, {threshold: 0});
      this.cartObserver.observe(this.querySelector('.product-add'));
      this.galleryObserver = new ResizeObserver(() => this.updateGallery());
      this.galleryObserver.observe(this.querySelector('.buybox'));
      this.galleryObserver.observe(this.querySelector('.gallery'));
      this.updateGallery();
      this.loadAllVariants();
    }
    // Liquid's product JSON stops at 250 variants; fetch the rest from the Storefront API.
    async loadAllVariants() {
      const total = Number(this.dataset.variantsCount);
      if (!total || this.variants.length >= total) return;
      const known = new Set(this.variants.map(v => String(v.id)));
      const query = 'query($handle:String!,$after:String,$country:CountryCode) @inContext(country:$country){product(handle:$handle){variants(first:250,after:$after){pageInfo{hasNextPage endCursor} nodes{id availableForSale selectedOptions{value} image{url} price{amount} compareAtPrice{amount}}}}}';
      let after = null;
      try {
        do {
          const response = await fetch('/api/2025-07/graphql.json', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({query, variables: {handle: this.product.handle, after, country: window.Shopify?.country || null}})});
          const page = (await response.json()).data.product.variants;
          page.nodes.forEach(node => {
            const id = node.id.split('/').pop();
            if (known.has(id)) return;
            known.add(id);
            this.variants.push({id: Number(id), options: node.selectedOptions.map(option => option.value), available: node.availableForSale, featured_image: node.image?.url || null, price: Math.round(node.price.amount * 100), compare_at_price: node.compareAtPrice ? Math.round(node.compareAtPrice.amount * 100) : null});
          });
          after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
        } while (after);
      } catch (error) {
        return;
      }
      this.update(false);
    }
    disconnectedCallback() {
      this.cartObserver?.disconnect();
      this.galleryObserver?.disconnect();
      this.stockVisibilityObserver?.disconnect();
      clearTimeout(this.stockSearchTimer);
    }
    updateGallery() {
      const track = this.querySelector('.gallery-track');
      const gallery = this.querySelector('.gallery');
      const boundary = this.querySelector('.bl-mobile-assurance') || this.querySelector('.product-add');
      if (!track || !gallery || !boundary) return;
      if (!matchMedia('(min-width: 701px)').matches) {
        track.style.removeProperty('--gallery-track-height');
        track.style.removeProperty('--gallery-height');
        return;
      }
      const galleryRect = gallery.getBoundingClientRect();
      // Keep the gallery beside the purchase area through the assurance card.
      const height = Math.max(galleryRect.height, boundary.getBoundingClientRect().bottom - track.getBoundingClientRect().top);
      track.style.setProperty('--gallery-track-height', `${height}px`);
      track.style.setProperty('--gallery-height', `${galleryRect.height}px`);
    }
    updateStock() {
      const row = this.querySelector('[data-stock-proof]');
      const copy = this.querySelector('[data-stock-copy]');
      const size = this.selected[this.sizeIndex];
      const isBundle = this.bundle && this.pack > 1;
      const colors = isBundle ? this.combos[this.pack] : [this.selected[this.colorIndex]];
      const keys = colors.map(color => [color, this.selected[this.lengthIndex], size].join('|'));
      const quantities = keys.map(key => this.lowStock[key]);
      const key = keys.join('§');
      const quantity = quantities.every(Number.isInteger) ? Math.max(5, Math.min(...quantities) + 3) : null;
      const available = this.bundle && this.pack > 1 ? colors.every(color => this.fitFor(color, true)?.available) : this.variant?.available;
      const threshold = Number(this.dataset.lowStockThreshold) || 10;
      const eligible = available && Number.isInteger(quantity) && quantity > 0 && quantity <= threshold;
      const selectionKey = `${key}|${quantity}|${Boolean(eligible)}`;
      if (this.stockSelectionKey === selectionKey) return;
      this.stockSelectionKey = selectionKey;
      clearTimeout(this.stockSearchTimer);
      this.stockVisibilityObserver?.disconnect();
      row.hidden = !eligible;
      copy.classList.remove('searching');
      copy.replaceChildren();
      if (!eligible) return;
      const showCount = () => {
        if (!this.isConnected || this.stockSelectionKey !== selectionKey) return;
        copy.classList.remove('searching');
        const count = document.createElement('span');
        count.className = 'stock-quantity';
        count.textContent = String(quantity);
        const unit = isBundle ? (quantity === 1 ? 'bundle' : 'bundles') : (quantity === 1 ? 'pair' : 'pairs');
        copy.replaceChildren(document.createTextNode('Only '), count, document.createTextNode(` ${unit} left in size ${size}`));
      };
      this.stockVisibilityObserver = new IntersectionObserver(entries => {
        if (this.stockSelectionKey !== selectionKey || !entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        this.stockVisibilityObserver.disconnect();
        copy.classList.add('searching');
        const dots = document.createElement('span');
        dots.className = 'stock-search-dots';
        dots.setAttribute('aria-hidden', 'true');
        copy.replaceChildren(document.createTextNode('Searching stock'), dots);
        this.stockSearchTimer = setTimeout(showCount, 1500);
      }, {threshold: 0.5});
      this.stockVisibilityObserver.observe(row);
    }

    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
    }
    matching(options) {
      return this.variants.filter(v => options.every((value, index) => value == null || v.options[index] === value));
    }
    update(writeURL = true) {
      const complete = this.selected.every(value => value != null);
      this.variant = complete ? this.matching(this.selected)[0] : null;
      const partial = this.matching(this.selected);
      const priced = this.variant || partial.find(v => v.available) || partial[0];
      this.querySelectorAll('[data-option]').forEach(button => {
        const index = Number(button.dataset.option) - 1;
        const active = this.selected[index] === button.dataset.value;
        button.classList.toggle('selected', active);
        button.setAttribute('aria-pressed', String(active));
        const candidate = [...this.selected]; candidate[index] = button.dataset.value;
        // Colors and lengths remain selectable even if the previous size is unavailable.
        if (index !== this.sizeIndex && this.sizeIndex >= 0) candidate[this.sizeIndex] = null;
        // A colour sold in fewer lengths stays selectable; choosing it moves the length (see click handler).
        if (index === this.colorIndex && this.lengthIndex >= 0) candidate[this.lengthIndex] = null;
        const exists = this.matching(candidate);
        button.disabled = exists.length === 0;
        button.classList.toggle('unavailable', exists.length > 0 && !exists.some(v => v.available));
        if (index === this.colorIndex) button.classList.toggle('sold-out', !this.variants.some(v => v.options[index] === button.dataset.value && v.available));
      });
      this.querySelectorAll('[data-option-label]').forEach(label => {
        const index = Number(label.dataset.optionLabel) - 1;
        label.textContent = this.selected[index] || 'Choose your size.';
        if (/length/i.test(this.optionNames[index])) label.textContent = (label.textContent.match(/\(([^)]+)\)/) || [null, label.textContent])[1];
      });
      if (priced) {
        const saving = priced.compare_at_price > priced.price ? Math[this.hasAttribute('data-anniversary') ? 'round' : 'floor']((priced.compare_at_price - priced.price) * 100 / priced.compare_at_price) : 0;
        this.querySelectorAll('[data-price]').forEach(el => el.textContent = this.money(priced.price));
        document.querySelector('bl-pdp-header [data-ann-price]')?.replaceChildren(this.money(priced.price));
        this.querySelectorAll('[data-compare]').forEach(el => { el.textContent = this.money(priced.compare_at_price || 0); el.hidden = saving === 0; });
        this.querySelectorAll('[data-saving]').forEach(el => { el.textContent = `SAVE ${saving}%`; el.hidden = saving === 0; });
        document.querySelector('bl-pdp-header [data-ann-save]')?.replaceChildren(saving ? `Save ${saving}% | ` : '');
      }
      const label = !complete ? 'CHOOSE MY SIZE →' : !this.variant ? 'UNAVAILABLE' : !this.variant.available ? 'SOLD OUT' : 'ADD TO CART →';
      this.querySelectorAll('[data-purchase]').forEach(button => {
        button.querySelector('[data-purchase-label]').textContent = this.pending ? 'ADDING…' : !complete ? 'ADD TO CART →' : label;
        button.disabled = Boolean(this.pending || (complete && (!this.variant || !this.variant.available)));
      });
      const fitParts = this.selected.map((value, index) => {
        if (!value) return /^size$/i.test(this.optionNames[index]) ? 'Choose your size' : `Choose ${this.optionNames[index].toLowerCase()}`;
        if (/^length$/i.test(this.optionNames[index])) return (value.match(/\(([^)]+)\)/) || [null, value])[1];
        return /^size$/i.test(this.optionNames[index]) ? `Size ${value}` : value;
      });
      this.querySelector('[data-purchase-fit]').textContent = fitParts.join(' · ');
      this.updateFollowers(complete, complete ? label : '', fitParts);
      this.updateStock();
      const idInput = this.querySelector('.form-shell input[name="id"]');
      idInput.disabled = !this.variant?.available;
      idInput.value = this.variant?.id || '';
      if (writeURL) {
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set('variant', this.variant.id); else url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
      if (this.sizeIndex >= 0 && this.selected[this.sizeIndex] != null) {
        this.classList.remove('needs-size');
        this.querySelector('.bl-size-alert')?.remove();
        this.querySelector('.sizes')?.classList.remove('bl-size-hint');
      }
      this.setStatus(complete && !this.variant ? 'This combination is unavailable. Choose another fit.' : complete && !this.variant.available ? 'This fit is currently sold out.' : '');
      if (this.bundle) this.updateBundle(complete, priced, fitParts);
    }
    moneyShort(cents) {
      return this.money(cents).replace(/[.,]00(?=\D*$)/, '');
    }
    // Variants for one colour in the chosen size and length: the exact one when the fit is complete, else every candidate.
    fitFor(color, exact = false) {
      const candidate = [...this.selected];
      candidate[this.colorIndex] = color;
      if (exact) return candidate.every(value => value != null) ? this.matching(candidate)[0] || null : null;
      return this.matching(candidate);
    }
    updateBundle(complete, priced, fitParts) {
      const n = this.pack;
      this.querySelectorAll('[data-pack]').forEach(button => {
        if (!button.classList.contains('bl-pack')) return;
        const on = Number(button.dataset.pack) === n;
        button.classList.toggle('selected', on);
        button.setAttribute('aria-pressed', String(on));
      });
      this.querySelector('[data-pack-label]').textContent = n === 1 ? 'Single' : `${n}-Pack`;
      this.querySelectorAll('[data-single-colors]').forEach(el => { el.hidden = n > 1; });
      [2, 3].forEach(k => {
        this.querySelectorAll(`[data-combo-field="${k}"],[data-combo-group="${k}"]`).forEach(el => { el.hidden = n !== k; });
        this.querySelector(`[data-combo-label="${k}"]`).textContent = this.combos[k].join(' + ');
      });
      this.querySelectorAll('[data-combo]').forEach(card => {
        const colors = card.dataset.combo.split('§');
        const on = card.dataset.combo === this.combos[card.dataset.packGroup].join('§');
        card.classList.toggle('selected', on);
        card.setAttribute('aria-pressed', String(on));
        const fits = colors.map(color => this.fitFor(color));
        card.disabled = fits.some(list => list.length === 0);
        card.classList.toggle('unavailable', !card.disabled && fits.some(list => !list.some(v => v.available)));
      });
      const unit = priced?.price;
      if (!unit) return;
      // Amounts off are stored in the shop currency; Shopify converts fixed-amount discounts at the same rate.
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const off = {1: 0, 2: Math.round(Number(this.dataset.twoOff) * rate), 3: Math.round(Number(this.dataset.threeOff) * rate)};
      const total = k => unit * k - off[k];
      const normal = Math.max(priced.compare_at_price || 0, unit);
      this.querySelector('[data-pack-each="1"]').textContent = this.money(unit);
      this.querySelector('[data-pack-save="1"]').textContent = `Save ${Math.round((normal - unit) * 100 / normal)}%`;
      const shipping = this.querySelector('[data-bundle-shipping]');
      shipping.textContent = n > 1 ? 'Free shipping unlocked!' : 'Add 1 more pair to unlock free shipping';
      shipping.classList.toggle('is-unlocked', n > 1);
      [2, 3].forEach(k => {
        this.querySelector(`[data-pack-each="${k}"]`).textContent = this.money(Math.floor(total(k) / k));
        this.querySelector(`[data-pack-save="${k}"]`).textContent = `Save ${Math.round((normal * k - total(k)) * 100 / (normal * k))}%`;
      });
      const note = this.querySelector('[data-bundle-note]');
      const strong = text => `<b>${text}</b>`;
      if (n === 1) note.innerHTML = `<button type="button" data-pack="2">Add a 2nd pair for just ${strong(this.moneyShort(total(2) - unit))} →</button>`;
      else if (n === 2) note.innerHTML = `Just ${strong(this.money(Math.floor(total(2) / 2)))} per pair · <button type="button" data-pack="3">Add a 3rd for just ${strong(this.moneyShort(total(3) - total(2)))} →</button>`;
      else note.innerHTML = `Just ${strong(this.money(Math.floor(total(3) / 3)))} per pair · Our best value`;
      if (n === 1) { this.packVariants = null; return; }
      // The crossed-out price is n times the single pair's own crossed-out price, so the saving grows with the pack.
      const compare = (priced.compare_at_price > unit ? priced.compare_at_price : unit) * n;
      const saving = Math.round((compare - total(n)) * 100 / compare);
      this.querySelectorAll('[data-price]').forEach(el => { el.textContent = this.money(total(n)); });
      this.querySelectorAll('[data-compare]').forEach(el => { el.textContent = this.money(compare); el.hidden = saving <= 0; });
      this.querySelectorAll('[data-saving]').forEach(el => { el.textContent = `SAVE ${saving}%`; el.hidden = saving <= 0; });
      const colors = this.combos[n];
      this.packVariants = complete ? colors.map(color => this.fitFor(color, true)) : null;
      const missing = complete && this.packVariants.some(v => !v);
      const soldOut = complete && !missing && this.packVariants.some(v => !v.available);
      const label = !complete ? 'ADD TO CART →' : missing ? 'UNAVAILABLE' : soldOut ? 'SOLD OUT' : 'ADD TO CART →';
      const parts = fitParts.map((part, index) => index === this.colorIndex ? colors.join(' + ') : part);
      if (n > 1) parts.push('Free shipping');
      this.querySelectorAll('[data-purchase]').forEach(button => {
        button.querySelector('[data-purchase-label]').textContent = this.pending ? 'ADDING…' : label;
        button.disabled = Boolean(this.pending || missing || soldOut);
      });
      this.querySelector('[data-purchase-fit]').textContent = parts.join(' · ');
      this.updateFollowers(complete, complete ? label : '', parts);
      document.querySelectorAll('[data-follow-purchase]').forEach(button => { button.disabled = Boolean(this.pending || missing || soldOut); });
      this.setStatus(missing ? 'One of these colors is not made in this fit. Choose another combo.' : soldOut ? 'One of these colors is sold out in this fit. Choose another combo.' : '');
    }
    setPack(n) {
      this.pack = n;
      if (n > 1) this.selected[this.colorIndex] = this.combos[n][0];
      this.update(false);
      if (this.colorGallery) this.showColor(this.selected[this.colorIndex]);
    }
    // Add To Cart buttons further down the page follow the main one: until every option is chosen they
    // name the next choice and send the shopper back up; once complete they read and act exactly like it.
    updateFollowers(complete, label, fitParts) {
      const missing = this.selected.findIndex(value => value == null);
      const missingName = missing > -1 ? this.optionNames[missing].toUpperCase() : '';
      const chosen = fitParts.filter((_, index) => this.selected[index] != null).join(' · ');
      document.querySelectorAll('[data-follow-purchase]').forEach(button => {
        button.querySelector('[data-follow-label]').textContent = this.pending ? 'ADDING…' : complete ? label : `CHOOSE YOUR ${missingName} ↑`;
        const fit = button.querySelector('[data-follow-fit]');
        fit.textContent = complete ? fitParts.join(' · ') : chosen;
        fit.hidden = !fit.textContent;
        button.classList.toggle('is-incomplete', !complete);
        button.disabled = Boolean(this.pending || (complete && (!this.variant || !this.variant.available)));
      });
    }
    setStatus(message) {
      this.querySelector('.bl-pdp-status').textContent = message;
    }
    updateImage(variant) {
      // Colour choices jump to that variant's image in the product's own media order.
      const media = variant?.featured_media;
      const src = variant?.featured_image?.src || variant?.featured_image;
      const path = url => String(url || '').split('?')[0].split('/').pop();
      const thumbs = [...this.querySelectorAll('.thumb')];
      const thumb = thumbs.find(button => media?.id && button.dataset.mediaId === String(media.id))
        || thumbs.find(button => typeof src === 'string' && path(button.dataset.photo) === path(src));
      if (thumb) this.showPhoto(thumb);
    }
    open(dialog) {
      this.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) other.close(); });
      if (!dialog.open) dialog.showModal();
    }
    handleClick(event) {
      if (this.bundle) {
        const pack = event.target.closest('[data-pack]');
        if (pack) { this.setPack(Number(pack.dataset.pack)); return; }
        const combo = event.target.closest('[data-combo]');
        if (combo) {
          if (combo.disabled) return;
          this.combos[combo.dataset.packGroup] = combo.dataset.combo.split('§');
          this.selected[this.colorIndex] = this.combos[combo.dataset.packGroup][0];
          this.update(false);
          if (this.colorGallery) this.showColor(this.selected[this.colorIndex]);
          return;
        }
      }
      const option = event.target.closest('[data-option]');
      if (option && !option.disabled) {
        const index = Number(option.dataset.option) - 1;
        this.selected[index] = option.dataset.value;
        // The chosen length does not exist in this colour: move to its first in-stock length, and drop a size it lacks.
        if (index === this.colorIndex && this.lengthIndex >= 0) {
          const loose = [...this.selected]; if (this.sizeIndex >= 0) loose[this.sizeIndex] = null;
          if (!this.matching(loose).length) {
            loose[this.lengthIndex] = null;
            const fit = this.matching(loose); const pick = fit.find(v => v.available) || fit[0];
            if (pick) this.selected[this.lengthIndex] = pick.options[this.lengthIndex];
            if (this.sizeIndex >= 0 && !this.matching(this.selected).length) this.selected[this.sizeIndex] = null;
          }
        }
        this.update();
        if (index === this.colorIndex) {
          if (this.colorGallery) this.showColor(option.dataset.value);
          else {
            const candidates = this.matching(this.selected);
            this.updateImage(candidates[0] || this.variants.find(v => v.options[index] === option.dataset.value));
          }
        }
        return;
      }
      const photo = event.target.closest('[data-photo]');
      if (photo) { this.showPhoto(photo); return; }
      const arrow = event.target.closest('.arrow');
      if (arrow) {
        const thumbs = [...this.querySelectorAll('.thumb:not([hidden])')];
        const current = Math.max(0, thumbs.findIndex(thumb => thumb.classList.contains('selected')));
        if (thumbs.length) this.showPhoto(thumbs[(current + (arrow.classList.contains('next') ? 1 : -1) + thumbs.length) % thumbs.length]);
      }
      // The sticky bar adds straight to cart once the fit is complete; otherwise it opens the options like every other Add To Cart.
      if (event.target.closest('[data-scroll-to-cart]')) this.purchase();
      if (event.target.closest('[data-close]')) event.target.closest('dialog').close();
      if (event.target.closest('[data-purchase]')) this.purchase();
    }
    showPhoto(thumb) {
      const main = this.querySelector('.main-image'); main.src = thumb.dataset.photo; main.removeAttribute('srcset');
      const thumbs = [...this.querySelectorAll('.thumb')];
      thumbs.forEach(button => button.classList.toggle('selected', button === thumb));
      // Keep the selected thumbnail inside the scrollable column.
      const column = thumb.parentElement;
      const top = thumb.offsetTop - column.offsetTop;
      if (top < column.scrollTop) column.scrollTo({top, behavior: 'smooth'});
      else if (top + thumb.offsetHeight > column.scrollTop + column.clientHeight) column.scrollTo({top: top + thumb.offsetHeight - column.clientHeight, behavior: 'smooth'});
      // On mobile, swipe to the page holding the same image.
      const page = this.mobileGallery?.querySelector(`.mobile-page:not([hidden]) [data-media-id="${thumb.dataset.mediaId}"]`)?.closest('.mobile-page');
      if (page && this.mobileGallery.offsetParent) this.mobileGallery.scrollTo({left: page.offsetLeft - this.mobileGallery.offsetLeft, behavior: 'smooth'});
    }
    // Colour gallery: show the chosen colour's photos followed by the shared ones, starting from its first photo.
    showColor(color, initial = false) {
      if (!color) return;
      const thumbs = [...this.querySelectorAll('.thumb')];
      thumbs.forEach(thumb => { thumb.hidden = Boolean(thumb.dataset.color) && thumb.dataset.color !== color; });
      this.mobileGallery.querySelectorAll('.mobile-page').forEach(page => { page.hidden = page.dataset.color !== color; });
      this.mobileGallery.scrollLeft = 0;
      this.mobileGallery.dispatchEvent(new Event('scroll'));
      const first = thumbs.find(thumb => !thumb.hidden);
      if (!first) return;
      if (initial && first.classList.contains('selected')) return;
      this.showPhoto(first);
      first.parentElement.scrollTop = 0;
    }
    // Missing size: show the alert right at the size buttons, shake them and scroll them into view.
    flagSize() {
      const sizes = this.querySelector('.sizes');
      if (!sizes) return;
      let alert = this.querySelector('.bl-size-alert');
      if (!alert) {
        alert = document.createElement('p');
        alert.className = 'bl-size-alert';
        alert.setAttribute('role', 'alert');
        sizes.before(alert);
      }
      alert.textContent = 'Please choose your size first';
      this.classList.remove('needs-size');
      void sizes.offsetWidth;
      this.classList.add('needs-size');
      this.setStatus('');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      alert.scrollIntoView({block: 'center', behavior: reduce ? 'instant' : 'smooth'});
      sizes.querySelector('.choice:not([disabled])')?.focus({preventScroll: true});
    }
    // One gentle wave over the size buttons the first time they scroll into view, so shoppers see where to start.
    hintSizes() {
      const sizes = this.querySelector('.sizes');
      if (!sizes || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        if (this.selected[this.sizeIndex] != null) return;
        setTimeout(() => { if (this.selected[this.sizeIndex] == null) sizes.classList.add('bl-size-hint'); }, 600);
      }, {threshold: 0.8});
      observer.observe(sizes);
    }
    // Every Add To Cart with a missing choice lands here: scroll so the product's Add To Cart sits at the bottom of the screen.
    revealOptions() {
      requestAnimationFrame(() => {
        const cta = this.querySelector('.product-add');
        if (!cta) return;
        const target = cta.getBoundingClientRect().bottom + window.scrollY + 8 - window.innerHeight;
        window.scrollTo({top: Math.max(0, target), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        const missing = this.selected.findIndex(value => value == null);
        this.querySelector(`[data-option="${missing + 1}"]:not([disabled])`)?.focus({preventScroll: true});
      });
    }
    async purchase() {
      if (this.pending) return;
      if (this.selected.some(value => value == null)) {
        if (this.sizeIndex >= 0 && this.selected[this.sizeIndex] == null) { this.flagSize(); return; }
        this.setStatus('Choose your size above to add these jeans to your cart.');
        this.revealOptions();
        return;
      }
      const items = this.bundle && this.pack > 1
        ? (this.packVariants || []).map(variant => ({id: variant?.id, quantity: 1, available: variant?.available}))
        : [{id: this.variant?.id, quantity: 1, available: this.variant?.available}];
      if (!items.length || items.some(item => !item.available)) return;
      this.pending = true;
      this.querySelectorAll('[data-purchase]').forEach(button => { button.disabled = true; button.querySelector('[data-purchase-label]').textContent = 'ADDING…'; });
      document.querySelectorAll('[data-follow-purchase]').forEach(button => { button.disabled = true; button.querySelector('[data-follow-label]').textContent = 'ADDING…'; });
      this.setStatus('');
      try {
        const lines = items.map(({id, quantity}) => ({id, quantity}));
        const drawer = document.querySelector('bl-pdp-cart');
        if (drawer?.add) {
          await drawer.add(lines);
          this.pending = false;
          this.update(false);
        } else {
          const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({items: lines}) });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Could not add this fit. Please try again.');
          window.location.assign(window.blOfferURL?.(this.dataset.cartUrl) || this.dataset.cartUrl);
        }
      } catch (error) {
        // Hide the snapshot after a failed add rather than displaying a potentially stale count.
        this.lowStock = {};
        this.pending = false; this.update(false);
        this.setStatus(error.message || 'Could not add this fit. Please try again.');
      }
    }
  }
  if (!customElements.get('bl-product')) customElements.define('bl-product', BootyliftProduct);
})();
